import { autocompletion, Completion, startCompletion } from '@codemirror/autocomplete'
import { hasError } from '@repo/shared/api/helpers'
import { PromptAllowedHelpers } from '@repo/shared/dtos/organization'
import { isEmptyString } from '@repo/shared/utils'
import { Button } from '@repo/ui/button'
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@repo/ui/card'
import { getLocale, useT } from '@repo/ui/hooks'
import { InlineCode } from '@repo/ui/inlineCode'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@repo/ui/select'
import { Sheet, SheetContent, SheetTrigger } from '@repo/ui/sheet'
import { cn, sortByKey } from '@repo/ui/utils'
import CodeMirror, { EditorView, Extension, keymap } from '@uiw/react-codemirror'
import { handlebarsLanguage } from '@xiechao/codemirror-lang-handlebars'
import { format } from 'date-fns'
import { isEqual } from 'lodash'
import { AlertCircle, FileClock, FilePen, History, Loader2 } from 'lucide-react'
import {
  KeyboardEvent,
  memo,
  MutableRefObject,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'
import CodeMirrorMerge from 'react-codemirror-merge'
import Markdown from 'react-markdown'

import {
  autocomplete,
  customTagsAutocomplete,
  generateCompletions,
  generateMarkdownDocs,
  highlightValidVariables,
} from '@/components/admin/autocompletion/helpers'
import { customTags } from '@/components/admin/autocompletion/sources'
import { api } from '@/main.tsx'
import { PromptTemplate } from '@/store/admin'

export interface PromptEditorProps {
  description?: string
  onPromptUpdated: (prompt: string) => void
  organizationId?: string
  placeholder: string
  promptTemplate: PromptTemplate
  title: string
  warn?: boolean
}

export function PromptEditor({
  description = 'Be careful when touching this 😅',
  onPromptUpdated,
  organizationId,
  placeholder,
  promptTemplate,
  title,
  warn = false,
}: PromptEditorProps) {
  const { tc } = useT()
  const [prompt, setPrompt] = useState(promptTemplate.promptText)
  const [oldPromptTemplate, setOldPromptTemplate] = useState<PromptTemplate | null>(null)
  const [isLoadingHistory, setIsLoadingHistory] = useState(false)
  const [history, setHistory] = useState<PromptTemplate[]>([])

  // Keep track of whether autocomplete is active
  const isCompletingRef = useRef(false)

  const handleSubmit = (e: { preventDefault: () => void }) => {
    e.preventDefault()
    if (!isDisabled) onPromptUpdated(prompt)
  }

  const submitOnPressMetaS = (e: KeyboardEvent<HTMLFormElement>) => {
    if (!warn && e && e.metaKey && e.key === 's') handleSubmit(e)
  }

  const isDisabled = isEmptyString(prompt) || isEqual(prompt, promptTemplate.promptText)

  const handleChange = useCallback((v: string) => {
    // Only update state if not currently showing completions
    if (!isCompletingRef.current) setPrompt(v)
  }, [])

  const OldPrompt = CodeMirrorMerge.Original
  const CurrentPrompt = CodeMirrorMerge.Modified

  const memoizedKeywords = useMemo(() => promptTemplate.keywords, [promptTemplate.keywords])

  const tags = PromptAllowedHelpers.map((v) => v.name)
  const validVariables = useMemo(
    () => [
      '!\\w+', // allow: {{!any comment}}
      ...tags.map((v) => `/${v}`), // allow: /if etc...
      ...memoizedKeywords
        .flatMap((k) => [
          k.isIterable ? null : k.apply,
          k.isIterable ? null : `../${k.apply}`, // allow: ../var
          ...tags.map((tag) => (k.isIterable ? `#${tag} ${k.apply}` : `#if ${k.apply}`)), // allow: #if etc...
        ])
        .filter((k) => k !== null),
    ],
    [memoizedKeywords],
  )

  const completions = useMemo(
    () => generateCompletions(memoizedKeywords).sort(sortByKey('label')),
    [memoizedKeywords],
  )

  const loadHistory = useCallback(async () => {
    if (organizationId && promptTemplate.promptTemplateType) {
      setIsLoadingHistory(true)
      const history = await api.admin.prompts.templates.getRevisions({
        organizationId,
        promptTemplateType: promptTemplate.promptTemplateType,
      })
      if (!hasError(history)) setHistory(history.sort((a, b) => b.revision - a.revision))
      setIsLoadingHistory(false)
    }
  }, [organizationId, promptTemplate.promptTemplateType])

  const selectOldPrompt = useCallback(
    (promptTemplateId: string) => {
      if (promptTemplateId === promptTemplate.id) {
        setOldPromptTemplate(null)
        return
      }
      const oldPromptTemplate = history.find((v) => v.id === promptTemplateId)
      if (oldPromptTemplate) {
        setOldPromptTemplate(oldPromptTemplate)
      }
    },
    [history, promptTemplate.id],
  )

  const defaultExtensions = useMemo(
    () => [handlebarsLanguage, EditorView.lineWrapping, highlightValidVariables(validVariables)],
    [validVariables],
  )

  const autocompleteExtensions = useMemo(
    () => [triggerCompletionOnSlash, autocompleteExtension(completions, isCompletingRef)],
    [completions],
  )

  const markdownDocs = useMemo(() => generateMarkdownDocs(completions), [completions])

  return (
    <div className="flex flex-1 flex-col gap-2 overflow-hidden">
      <Card className="flex flex-1 flex-col overflow-hidden">
        <CardHeader className="flex flex-row items-start px-4">
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
          <CardAction>
            <InlineCode className="select-none text-sm mr-2">
              Current revision: {promptTemplate.revision}
            </InlineCode>
            {history.length > 0 ? (
              <Select onValueChange={selectOldPrompt} defaultValue={undefined}>
                <SelectTrigger className="max-w-fit">
                  <SelectValue placeholder="Browse Revision History" />
                </SelectTrigger>
                <SelectContent>
                  {history.map((v) => (
                    <SelectItem value={v.id} key={v.id} className="pl-2">
                      {v.id === promptTemplate.id ? (
                        <div className="flex items-center">
                          <FilePen className="mr-2 size-4" />
                          Current Revision
                        </div>
                      ) : (
                        <div className="flex items-center">
                          <FileClock className="mr-2 size-4" />
                          Revision
                          <span className="pl-1 font-bold">{v.revision}</span>
                          <span className="ml-2 text-xs text-gray-500">
                            {format(v.revisionDate, 'PPPP p', {
                              locale: getLocale(),
                            })}
                          </span>
                        </div>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Button
                onClick={() => void loadHistory()}
                disabled={isLoadingHistory}
                className="w-fit"
                variant="outline"
              >
                {isLoadingHistory ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <History className="size-4" />
                )}
                {isLoadingHistory ? 'Loading History' : 'Load History'}{' '}
              </Button>
            )}

            <Sheet>
              <SheetTrigger asChild>
                <Button>Documentation</Button>
              </SheetTrigger>
              <SheetContent side="right" className="min-w-[50vw]">
                <Documentation markdownDocs={markdownDocs} />
              </SheetContent>
            </Sheet>
          </CardAction>
        </CardHeader>
        <div>
          <form onSubmit={handleSubmit} onKeyDown={submitOnPressMetaS}>
            <CardContent className="flex max-w-full flex-1 flex-col">
              {warn && (
                <div className="mb-2 flex items-center rounded-md border border-red-200 bg-red-50 p-4 text-red-700">
                  <AlertCircle className="mr-2" />
                  Caution: you're editing the Global Master Prompt!!!
                </div>
              )}

              {oldPromptTemplate ? (
                <>
                  <div className="flex w-full flex-row">
                    <div className="w-1/2">
                      <div className="flex items-center pl-2">
                        <FileClock className="mr-4 size-5" />
                        <span className="text-lg">
                          Old Prompt{' '}
                          <span className="text-sm text-gray-500">
                            (revision {oldPromptTemplate.revision})
                          </span>
                        </span>
                      </div>
                    </div>
                    <div className="w-1/2">
                      <div className="flex items-center pl-2">
                        <FilePen className="mr-4 size-5" />
                        <span className="text-lg">Current Prompt</span>
                      </div>
                    </div>
                  </div>
                  <CodeMirrorMerge className="max-h-[65vh] overflow-hidden overflow-y-auto">
                    <OldPrompt
                      extensions={defaultExtensions}
                      readOnly
                      editable={false}
                      value={oldPromptTemplate.promptText}
                    />
                    <CurrentPrompt
                      basicSetup={{ indentOnInput: false }}
                      extensions={[
                        ...defaultExtensions,
                        ...autocompleteExtensions,
                        EditorView.updateListener.of((v) => {
                          // because onChange does not work
                          if (v.docChanged) handleChange(v.state.doc.toString())
                        }),
                      ]}
                      placeholder={placeholder}
                      value={prompt}
                    />
                  </CodeMirrorMerge>
                </>
              ) : (
                <CodeMirror
                  basicSetup={{ indentOnInput: false }}
                  extensions={[...defaultExtensions, ...autocompleteExtensions]}
                  maxHeight="70vh"
                  minWidth="100%"
                  onChange={handleChange}
                  placeholder={placeholder}
                  value={prompt}
                />
              )}
            </CardContent>
            <CardFooter
              className={cn(oldPromptTemplate ? 'flex justify-between gap-4' : 'justify-end')}
            >
              {oldPromptTemplate && (
                <Button
                  variant="outline"
                  onClick={() => onPromptUpdated(oldPromptTemplate.promptText)}
                >
                  <History className="size-4" />
                  Rollback to Revision
                  <span className="ml-1 font-bold">{oldPromptTemplate.revision}</span>
                </Button>
              )}
              <Button type="submit" disabled={isDisabled} isSensitive={warn} onClick={handleSubmit}>
                {tc('update', { cfl: true })}
              </Button>
            </CardFooter>
          </form>
        </div>
      </Card>
    </div>
  )
}

const Documentation = memo(function ({ markdownDocs }: { markdownDocs: string }) {
  return (
    <div className="flex flex-1 flex-col gap-2 overflow-hidden overflow-y-auto rounded-md bg-white bg-opacity-90 p-4">
      <Markdown
        components={{
          h1: ({ ...props }) => <h1 className="text-primary text-2xl font-bold" {...props} />,
          h2: ({ ...props }) => (
            <h2
              className="text-primary border-primary-200 border-t-4 pt-4 text-xl font-semibold"
              {...props}
            />
          ),
          h3: ({ ...props }) => (
            <h3
              className="text-primary border-primary-200 border-b pb-px text-lg font-medium"
              {...props}
            />
          ),
          p: ({ ...props }) => <p className="text-primary text-base leading-relaxed" {...props} />,
          ul: ({ ...props }) => (
            <ul className="text-primary mb-2 list-outside list-disc space-y-px px-4" {...props} />
          ),
          li: ({ ...props }) => <li className="p-0 leading-tight" {...props} />,
          ol: ({ ...props }) => (
            <ol className="text-primary mb-2 list-inside list-decimal space-y-px" {...props} />
          ),
          pre: ({ ...props }) => (
            <pre
              className="text-primary bg-primary-100 border-primary-200 m-0 ml-2 rounded-md border p-2 text-sm"
              {...props}
            />
          ),
        }}
      >
        {markdownDocs}
      </Markdown>
    </div>
  )
})

function autocompleteExtension(
  completions: Completion[],
  isCompletingRef: MutableRefObject<boolean>,
): Extension {
  return autocompletion({
    activateOnTyping: true,
    override: [autocomplete(completions, isCompletingRef), customTagsAutocomplete(customTags)],
    defaultKeymap: true,
    activateOnCompletion() {
      isCompletingRef.current = false
      return true
    },
  })
}

const triggerCompletionOnSlash: Extension = keymap.of([
  {
    key: 'Mod-k',
    run: (view) => {
      startCompletion(view)
      return false
    },
  },
])
